/**
 * The AI read, on the screen's side: what to send, when to send it, and what to
 * do with what comes back.
 *
 * Pure by construction and unit-tested next door, for the same reason
 * `format.ts` is: "which control may a machine fill in" is a rule about who
 * owns a decision, and a rule like that must not live inside a component where
 * the only way to check it is to press a button and squint. Since the call
 * became automatic — it fires the moment a sheet's trace lands, not on an
 * operator's press — "when to send it" is a rule of exactly the same kind, and
 * `suggestRunKey` is here rather than in the component for exactly that reason.
 *
 * ── The rule ─────────────────────────────────────────────────────────────────
 * A suggestion may fill a control the operator has NOT answered, and nothing
 * else. For the material that is unambiguous — `materialId: null` is exactly
 * "nobody chose one". For the quantity it is not, because every traced piece
 * starts at the sensible default of 2, so `2` cannot be told apart from a typed
 * 2; the caller therefore says whether the value is committed (a part that came
 * back from the API has been through a human's save; a shape traced two seconds
 * ago has not).
 *
 * Every control a suggestion touches is marked, the mark is shown as a quiet
 * "öneri" chip, and the first human edit of that field removes it. So the state
 * an operator can leave the screen in is always one of two honest ones: this
 * value is mine, or this value is a suggestion I have not looked at yet.
 *
 * ── The floor ────────────────────────────────────────────────────────────────
 * Below `MIN_SUGGESTION_CONFIDENCE` nothing at all happens — no fill. A model
 * that says it is unsure and is believed anyway is worse than no model: the
 * operator's real job on this screen is checking, and a control that is
 * already full is a control that gets checked less.
 */

import type {
  PartGeom,
  PieceSuggestion,
  SayaMaterialDto,
  SuggestedMaterial,
  SuggestPieceInput,
  TraceFinding,
  TraceFindingKind,
  TraceReview,
} from './api'
import { DEFAULT_PART_VALUES, formatCount, mm2ToDesi, type PartFormValues } from './format'
import { interiorPoint, mmRingToScanPx, ringsBounds } from './geometry'

/* ── Policy ─────────────────────────────────────────────────────────────────── */

/**
 * How sure the model has to be before it may touch a control.
 *
 * Half. Not a tuned number — a stated one: at even odds a suggestion is worth
 * showing, below them it is noise dressed as an answer, and the model's own
 * `belirsiz` already covers the case where it knows it does not know.
 */
export const MIN_SUGGESTION_CONFIDENCE = 0.5

/** The chip's word, in one place so every surface that grows one agrees. */
export const SUGGESTION_CHIP_TEXT = 'öneri'

/** What the chip says when it is pointed at. */
export const SUGGESTION_CHIP_TITLE = 'Yapay zekâ önerisi — elle değiştirince kalkar.'

/* ── Per-piece marks ────────────────────────────────────────────────────────── */

/** Which of a row's controls the suggestion filled and a human has not touched. */
export interface SuggestedFields {
  material: boolean
  qty: boolean
}

/** The state every piece starts in and returns to once it has been edited. */
export const NO_SUGGESTED_FIELDS: SuggestedFields = { material: false, qty: false }

/** True when this row still carries any unreviewed suggestion. */
export function hasSuggestion(fields: SuggestedFields): boolean {
  return fields.material || fields.qty
}

/**
 * The marks that survive an edit.
 *
 * A human touching a field is what retires its chip — and only its own chip: an
 * operator who picks a material has said nothing about the adet beside it, so
 * that one still reads as a suggestion nobody has checked.
 */
export function clearSuggested(
  fields: SuggestedFields,
  patch: Partial<PartFormValues>,
): SuggestedFields {
  if (!hasSuggestion(fields)) return fields
  const material = fields.material && !('materialId' in patch)
  const qty = fields.qty && !('qtyPerPair' in patch)
  return material === fields.material && qty === fields.qty ? fields : { material, qty }
}

/**
 * Has a human answered this row's adet?
 *
 * There are two ways to have done so, and the rule is only safe when it counts
 * both. A part that came back from the API carries somebody's SAVED figure. A
 * shape traced two minutes ago carries the trace's default of 2 until the
 * operator types over it — and that unsaved keystroke is every bit as much an
 * answer. Testing only "was it saved" is how a suggestion of 2 lands on top of
 * a typed 6, which is precisely the one thing the merge exists not to do; it
 * matters more now that the answer can arrive while the operator is typing.
 *
 * A deliberately typed 2 still reads as untouched. That case cannot be told
 * apart from the default by construction, and does not need to be: the only
 * value a suggestion could write there is the value already there.
 *
 * Lives here rather than in the screen because it is a rule about who owns a
 * decision, and a rule like that must be checkable without pressing a button.
 */
export function qtyAnswered(options: { qtyPerPair: number; saved: boolean }): boolean {
  return options.saved || options.qtyPerPair !== DEFAULT_PART_VALUES.qtyPerPair
}

/* ── Material vocabulary → the revision's own materials ─────────────────────── */

/** Turkish casing: `I`→`ı` and `İ`→`i`, which a plain `toLowerCase` gets wrong. */
function fold(name: string): string {
  return name.toLocaleLowerCase('tr')
}

function looksLikeAstar(material: SayaMaterialDto): boolean {
  return fold(material.name).includes('astar')
}

/**
 * The model names a trade word; the rail needs a row of `app.saya_material`.
 *
 * The model is deliberately never shown the material table — it changes, it is
 * full of stock codes, and a model asked to pick from it would be guessing at
 * inventory rather than reading a photograph. So it answers `deri`/`astar` and
 * the matching happens here, against the names the factory typed.
 *
 * `astar` matches on the word. `deri` is whatever is bought by the desi and is
 * NOT an astar — leather is the desi good, and a lining card that happens to be
 * priced that way must not win the leather slot. Anything unmatched returns
 * null and simply fills nothing, which is the same outcome as `belirsiz`: a
 * wrong material is a wrong cut, and no material is a nag on the approval list.
 */
export function materialIdForSuggestion(
  material: SuggestedMaterial,
  materials: readonly SayaMaterialDto[],
): number | null {
  if (material === 'belirsiz') return null

  if (material === 'astar') {
    return materials.find(looksLikeAstar)?.id ?? null
  }

  const named = materials.find(
    (candidate) => fold(candidate.name).includes('deri') && !looksLikeAstar(candidate),
  )
  if (named) return named.id
  const byUnit = materials.find(
    (candidate) => candidate.purchaseUnit === 'desi' && !looksLikeAstar(candidate),
  )
  return byUnit?.id ?? null
}

/* ── The request ────────────────────────────────────────────────────────────── */

/**
 * One piece described in the scan's own pixel space — the frame of the
 * photograph the model is about to be shown.
 *
 * The interior point is the same one the canvas draws the piece's number on
 * (`interiorPoint`), so "the piece under this dot" means to the model exactly
 * what the number on screen means to the operator. Null when the shape is too
 * degenerate to hang a point on, which is a shape nobody could label either.
 */
export function suggestPieceInput(options: {
  index: number
  geom: PartGeom
  netMm2: number
  pxHeight: number
  pxPerMmX: number
  pxPerMmY: number
}): SuggestPieceInput | null {
  const { pxPerMmX, pxPerMmY, pxHeight } = options
  // Outer ring only — holes are inert legacy cargo and the canvas no longer
  // draws them, so the dot has to agree with the badge the operator sees.
  const outer = mmRingToScanPx(options.geom.outer, pxPerMmX, pxPerMmY, pxHeight)

  const inside = interiorPoint(outer)
  const bounds = ringsBounds([outer])
  if (!inside || !bounds) return null

  return {
    index: options.index,
    interiorPx: inside,
    bboxPx: [bounds.minX, bounds.minY, bounds.maxX, bounds.maxY],
    netDesi: mm2ToDesi(options.netMm2),
  }
}

/* ── Asking once ────────────────────────────────────────────────────────────── */

/**
 * The identity of one question: this sheet, with these shapes on it.
 *
 * The screen asks automatically the moment a trace lands, and this is what
 * keeps "automatically" from meaning "again on every render". It deliberately
 * covers the GEOMETRY and nothing else — a material picked, an adet typed, a
 * piece renamed, a save that turns traced shapes into stored parts: none of
 * those change the question, and re-asking after any of them would spend a call
 * to be told the same thing.
 *
 * What it must survive is precisely that save. The Worker stores millimetres
 * rounded to 0,01 mm and recomputes the areas itself, so a key built from raw
 * coordinates or from `netMm2` would change under the round trip and fire a
 * second call the moment the operator pressed Kaydet. Point counts and a
 * whole-millimetre box do not move.
 *
 * A shape genuinely re-traced — a re-shot sheet, a different scan — lands on a
 * different scan id anyway, which is the first thing in the key.
 */
export function suggestRunKey(scanId: number, geoms: readonly PartGeom[]): string {
  let hash = 0x811c9dc5
  for (const geom of geoms) {
    const bounds = ringsBounds([geom.outer])
    const shape = bounds
      ? `${geom.outer.length}/${geom.holes.length}@${Math.round(bounds.minX)},${Math.round(bounds.minY)},${Math.round(bounds.maxX)},${Math.round(bounds.maxY)};`
      : 'empty;'
    for (let index = 0; index < shape.length; index += 1) {
      hash ^= shape.charCodeAt(index)
      hash = Math.imul(hash, 0x01000193)
    }
  }
  return `${scanId}.${(hash >>> 0).toString(36)}.${geoms.length}`
}

/* ── The review of our own tracing ──────────────────────────────────────────── */

/** What each kind of finding is called on screen. */
export const TRACE_FINDING_LABELS: Record<TraceFindingKind, string> = {
  eksik_parca: 'Eksik parça',
  kotu_kontur: 'Kontur hatalı',
  birlesik_parca: 'Parçalar birleşmiş',
  diger: 'Diğer',
}

/**
 * The whole note when the model has nothing against our drawing.
 *
 * "Saw no problem", deliberately, and not "approved". The difference is the
 * whole standing of this line: a model that looked and reported nothing is a
 * fact about a machine, an approval is a fact about the drawing — and the sheet
 * it read is a photograph of paper anybody can write on, so a quiet sentence
 * asking to be believed is one of the things a scanned slip could ask for.
 * Approval on this screen is the operator's tick and the "Onayla" the revision
 * goes through, and nothing a model says may borrow that word.
 */
export const TRACE_REVIEW_OK_TEXT = 'Yapay zekâ çizimde sorun görmedi'

/**
 * The line on the note's spine — what the operator decides whether to open on.
 *
 * A bare count, `N not`, in the same vocabulary as the bar's "tarama notu":
 * with the blocking machinery gone a finding is a note, not an alarm, and the
 * count is the only part that says how long opening it will take. A `sorunlu`
 * with nothing left in the list (every finding was dropped as unreadable)
 * still says something is wrong: the verdict is the model's, and swallowing it
 * would be us overruling it on the strength of our own parser.
 */
export function traceReviewSummary(review: TraceReview): string {
  if (review.overall === 'iyi') return TRACE_REVIEW_OK_TEXT
  if (review.findings.length === 0) return 'Yapay zekâ çizimde sorun gördü'
  return `${formatCount(review.findings.length)} not`
}

/**
 * One finding as a line: which piece, what kind, and the model's own sentence.
 *
 * `pieceIndex` is a rail position, so it is shown +1 — the same number the
 * canvas draws on the shape. A finding about no piece (the valuable kind: a
 * kalıp nothing traced) simply has no prefix.
 */
export function traceFindingLine(finding: TraceFinding): string {
  const where = finding.pieceIndex === null ? '' : `Parça ${formatCount(finding.pieceIndex + 1)} · `
  return `${where}${TRACE_FINDING_LABELS[finding.kind]} — ${finding.detail}`
}

/* ── The merge ──────────────────────────────────────────────────────────────── */

/** One row as the merge sees it: what it holds, and what is already answered. */
export interface PrefillTarget {
  key: string
  /** The piece's place in the rail — how a suggestion addresses it. */
  index: number
  values: PartFormValues
  /**
   * True when the quantity is a human's answer rather than the trace's default.
   * A part that came back from the API has been saved by somebody; a shape
   * traced in this session has not.
   */
  qtyCommitted: boolean
}

/** One row's worth of change, ready to apply. */
export interface PrefillPatch {
  key: string
  patch: Partial<PartFormValues>
  /** The chips this patch earns — only the fields it actually wrote. */
  filled: SuggestedFields
}

export interface PrefillResult {
  /** Rows with something to fill. A row that had nothing free is absent. */
  patches: PrefillPatch[]
}

/**
 * Suggestions → the changes the screen may make.
 *
 * A suggestion for a piece that is not on this sheet, or one below the
 * confidence floor, does nothing at all.
 *
 * Nothing here mutates: it returns patches, and the caller applies them through
 * the same `updatePiece` path a human edit goes through. That is what keeps one
 * dirty-tracking story on the screen instead of two.
 */
export function prefillFromSuggestions(
  targets: readonly PrefillTarget[],
  suggestions: readonly PieceSuggestion[],
  materials: readonly SayaMaterialDto[],
): PrefillResult {
  const byIndex = new Map(targets.map((target) => [target.index, target]))
  const patches: PrefillPatch[] = []

  for (const suggestion of suggestions) {
    if (suggestion.confidence < MIN_SUGGESTION_CONFIDENCE) continue
    const target = byIndex.get(suggestion.index)
    if (!target) continue

    const patch: Partial<PartFormValues> = {}
    const filled: SuggestedFields = { material: false, qty: false }

    if (target.values.materialId == null) {
      const materialId = materialIdForSuggestion(suggestion.material, materials)
      if (materialId != null) {
        patch.materialId = materialId
        filled.material = true
      }
    }

    if (!target.qtyCommitted && suggestion.qtyPerPair != null) {
      patch.qtyPerPair = suggestion.qtyPerPair
      filled.qty = true
    }

    if (filled.material || filled.qty) patches.push({ key: target.key, patch, filled })
  }

  return { patches }
}

/**
 * What the operator is told the suggestion did, in one line.
 *
 * It reports the OUTCOME rather than the request: "the model answered about
 * nine pieces" is a fact about a machine, and "three fields were filled" is a
 * fact about the work in front of them.
 */
export function prefillSummary(result: PrefillResult, suggestionCount: number): string {
  if (suggestionCount === 0) {
    return 'Yapay zekâ bu taramada okunacak bir şey bulamadı.'
  }

  if (result.patches.length === 0) {
    return 'Öneri geldi, ama doldurulacak boş alan kalmamış.'
  }
  return `${formatCount(result.patches.length)} parça dolduruldu — kontrol edip kaydedin.`
}
